import { supabase } from './supabaseClient';
import { hardResetAuth, safeGetSession, safeGetUser, safeRefreshSession } from './supabaseAuthSafe';
import { logAuthDebug } from './authDebugLogger';
import { isDefinitelyInvalidAuthSessionError } from './supabaseAuthErrorClassifier';
import { localDevWarn } from './debug/localDevLogger';

// Check if user is already authenticated (validated with the server)
const buildAuthResult = (user, status) => ({ user: user || null, status });

export const getCurrentUser = async () => {
  logAuthDebug('get_current_user_start');
  // First check if we even have a local session; if not, don't treat it as an error.
  let sessionData;
  try {
    const res = await safeGetSession(supabase, { timeoutMs: 3000 });
    sessionData = res?.data;
    logAuthDebug('get_session', { sessionData });
  } catch (error) {
    logAuthDebug('get_session_timeout', { error: error?.message || error });
    return buildAuthResult(null, 'unknown');
  }
  if (!sessionData?.session) return buildAuthResult(null, 'none');

  try {
    const { data, error } = await safeGetUser(supabase, { timeoutMs: 7000 });
    logAuthDebug('get_user', { error, hasUser: Boolean(data?.user) });
    if (error) {
      // IMPORTANT:
      // `getSession()` already found a local session. If `getUser()` fails, it might be
      // a transient network issue (adblock/VPN/firewall/offline) rather than a truly
      // invalid token. Only clear auth storage when we're confident the session is invalid.
      if (isDefinitelyInvalidAuthSessionError(error)) {
        logAuthDebug('invalid_session_detected', { error: error?.message });
        // Try a refresh first in case the access token just expired.
        try {
          const refreshRes = await safeRefreshSession(supabase, { timeoutMs: 7000 });
          logAuthDebug('refresh_session', { error: refreshRes?.error, session: refreshRes?.data?.session });
          const refreshedSession = refreshRes?.data?.session;
          if (refreshedSession?.access_token) {
            const retry = await safeGetUser(supabase, { timeoutMs: 7000 });
            logAuthDebug('get_user_retry', { error: retry?.error, hasUser: Boolean(retry?.data?.user) });
            if (!retry?.error && retry?.data?.user) {
              return buildAuthResult(retry.data.user, 'ok');
            }
          }
        } catch {
          // ignore and fall through to clearing local session
        }

        localDevWarn('Auth session invalid after refresh (clearing local session):', error.message);
        // Hard reset (local) to clear stuck tokens; don't hang on network signOut.
        await hardResetAuth(supabase);
        return buildAuthResult(null, 'none');
      }

      localDevWarn(
        'Auth check returned an error, but keeping local session (will retry later):',
        error.message
      );
      return buildAuthResult(sessionData.session.user || null, 'unknown');
    }
    return buildAuthResult(data?.user || null, data?.user ? 'ok' : 'none');
  } catch (error) {
    // If auth is temporarily unreachable, don't nuke the session.
    localDevWarn('Auth check failed (will retry later):', error?.message || error);
    return buildAuthResult(sessionData.session.user || null, 'unknown');
  }
};

// Ensure user is authenticated (no auto sign-up for guests)
export const ensureAuthenticated = async () => {
  try {
    logAuthDebug('ensure_authenticated_start');
    return await getCurrentUser();
  } catch (error) {
    console.error('Authentication error:', error);
    logAuthDebug('ensure_authenticated_error', { error: error?.message || error });
    return buildAuthResult(null, 'unknown');
  }
};